<template>
  <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 w-full text-slate-700">
    <!-- 1. Player Status & Account Type Section -->
    <div
      class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-start gap-1"
    >
      <div
        class="flex items-center gap-3 pb-3 mb-3 border-b border-slate-200/60"
      >
        <div
          class="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-sm font-bold shadow-xs"
        >
          <i class="fa-solid fa-user-gear"></i>
        </div>
        <div>
          <h3 class="text-sm font-bold text-slate-800 m-0">
            Player Status & Account
          </h3>
          <p class="text-xs text-slate-400 m-0">
            Manage player account type and operational status
          </p>
        </div>
      </div>

      <div class="flex gap-4">
        <!-- Status Selection -->
        <div class="flex flex-col gap-1.5 w-full">
          <label
            for="status-select"
            class="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"
          >
            <i class="fa-solid fa-circle-dot text-blue-500 text-[10px]"></i>
            Change Status
          </label>
          <div class="relative">
            <select
              id="status-select"
              v-model="selectedStatus"
              class="w-full bg-white text-slate-800 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none cursor-pointer"
            >
              <option
                v-for="status in playerStatus"
                :key="status.status"
                :value="status.status"
              >
                {{
                  status.label.charAt(0).toUpperCase() + status.label.slice(1)
                }}
              </option>
            </select>
            <i
              class="fa-solid fa-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none"
            ></i>
          </div>
        </div>

        <!-- Frozen Date Picker -->
        <div
          v-if="selectedStatus === 'frozen'"
          class="flex flex-col gap-1.5 w-full"
        >
          <label
            for="frozen-date"
            class="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"
          >
            <i class="fa-solid fa-snowflake text-sky-500 text-[10px]"></i>
            Frozen Date & Time
          </label>
          <div class="relative">
            <flat-pickr
              id="frozen-date"
              v-model="frozenAt"
              :config="{
                enableTime: true,
                dateFormat: 'Y-m-d H:i',
                time_24hr: true,
                minDate: new Date(),
              }"
              class="w-full bg-white text-slate-800 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              placeholder="Select date and time"
            />
            <i
              class="fa-solid fa-calendar-days absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none"
            ></i>
          </div>
        </div>

        <!-- Account Type Selection -->
        <div class="flex flex-col gap-1.5 w-full">
          <label
            for="account-type-select"
            class="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"
          >
            <i class="fa-solid fa-id-badge text-purple-500 text-[10px]"></i>
            Account Type
          </label>
          <div class="relative">
            <select
              id="account-type-select"
              v-model="selectedStatusAccount"
              class="w-full bg-white text-slate-800 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none cursor-pointer"
            >
              <option
                v-for="status in playerAccountStatus"
                :key="status.status"
                :value="status.status"
              >
                {{
                  status.label.charAt(0).toUpperCase() + status.label.slice(1)
                }}
              </option>
            </select>
            <i
              class="fa-solid fa-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none"
            ></i>
          </div>
        </div>
      </div>

      <div class="pt-2">
        <button
          @click="changeStatus"
          class="w-full sm:w-auto px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
        >
          <i class="fa-solid fa-check"></i>
          Save Changes
        </button>
      </div>
    </div>

    <!-- 2. Block/Ban Management Section -->
    <div
      v-if="!playerInfo.is_banned_chat"
      class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 flex flex-col justify-start gap-1"
    >
      <div
        class="flex items-center gap-3 pb-3 mb-3 border-b border-slate-200/60"
      >
        <div
          class="w-9 h-9 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center text-sm font-bold shadow-xs"
        >
          <i class="fa-solid fa-user-slash"></i>
        </div>
        <div>
          <h3 class="text-sm font-bold text-slate-800 m-0">
            Chat Restriction / Ban
          </h3>
          <p class="text-xs text-slate-400 m-0">
            Apply restriction to
            <strong class="text-slate-600">{{ playerInfo.user_id }}</strong> for
            chat usage
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Block Duration -->
          <div class="flex flex-col gap-1.5">
            <label
              class="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"
            >
              <i class="fa-solid fa-clock text-amber-500 text-[10px]"></i>
              Duration (minutes)
            </label>
            <input
              v-model="blockTime"
              type="number"
              class="w-full bg-white text-slate-800 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              placeholder="0 (Unlimited)"
              min="0"
            />
          </div>

          <!-- Reason -->
          <div class="flex flex-col gap-1.5">
            <label
              class="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"
            >
              <i
                class="fa-solid fa-triangle-exclamation text-rose-500 text-[10px]"
              ></i>
              Reason
            </label>
            <div class="relative">
              <select
                v-model="blockReason"
                class="w-full bg-white text-slate-800 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none cursor-pointer"
                @change="onBlockReasonChange"
              >
                <option v-for="reason in reasons" :key="reason" :value="reason">
                  {{ reason }}
                </option>
              </select>
              <i
                class="fa-solid fa-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none"
              ></i>
            </div>
          </div>
        </div>

        <!-- Custom Reason Textarea -->
        <div v-if="blockReason === 'Other'" class="flex flex-col gap-1.5">
          <label
            class="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5"
          >
            <i class="fa-solid fa-message text-slate-400 text-[10px]"></i>
            Custom Reason Details
          </label>
          <textarea
            v-model="customNote"
            class="w-full bg-white text-slate-800 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            placeholder="Please specify the reason for blocking this user..."
            rows="3"
          ></textarea>
        </div>
      </div>

      <div class="pt-2">
        <button
          class="w-full sm:w-auto px-6 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
          @click="handleBannedPlayer(playerInfo?.user_id)"
        >
          <i class="fa-solid fa-ban"></i>
          Ban User
        </button>
      </div>
    </div>

    <!-- 3. Unban Section if already banned -->
    <div
      v-else
      class="bg-rose-50/70 border border-rose-200/80 rounded-2xl p-5 flex flex-col justify-between gap-4"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-9 h-9 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center text-sm font-bold"
        >
          <i class="fa-solid fa-ban"></i>
        </div>
        <div>
          <h3 class="text-sm font-bold text-rose-800 m-0">
            User is currently banned from chat
          </h3>
          <p class="text-xs text-rose-600 m-0">
            This player has active chat restrictions.
          </p>
        </div>
      </div>

      <div>
        <button
          class="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs uppercase tracking-wider rounded-xl shadow-xs transition-all flex items-center gap-2 cursor-pointer"
          @click="handleBannedPlayer(playerInfo?.user_id)"
        >
          <i class="fa-solid fa-lock-open"></i>
          Unban User
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import FlatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Swal from "sweetalert2";
import { postFetch } from "@/api/axiosClient";

export default {
  name: "ModernPlayerSettings",
  props: {
    playerInfo: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedStatus: null,
      selectedStatusAccount: null,
      frozenAt: null,
      playerStatus: [
        { status: "active", label: "active" },
        { status: "blocked", label: "blocked" },
        { status: "banned", label: "banned" },
        { status: "frozen", label: "frozen" },
      ],
      playerAccountStatus: [
        { status: "test", label: "Is test" },
        { status: "casual", label: "Casual" },
      ],
      reasons: ["Spam", "Offensive", "Flood", "Other"],
      blockReason: "Spam",
      blockTime: 0,
      customNote: "",
    };
  },
  components: {
    FlatPickr,
  },
  mounted() {
    this.frozenAt = this.playerInfo?.frozen_at || null;
    this.selectedStatus = this.playerInfo?.status || null;
    this.selectedStatusAccount =
      this.playerInfo?.is_test === true ? "test" : "casual";
  },
  methods: {
    onBlockReasonChange() {
      if (this.blockReason !== "Other") {
        this.customNote = "";
      }
    },
    async changeStatus() {
      const params = {
        status: this.selectedStatus,
        is_banned_chat: this.playerInfo.is_banned_chat,
        skin_id: this.playerInfo.skin_id,
        is_test: this.selectedStatusAccount === "test",
      };

      if (this.selectedStatus === "frozen") {
        params.frozen_at = this.frozenAt;
      }

      try {
        await postFetch(`players/${this.playerInfo.user_id}`, params);

        await Swal.fire({
          title: "Success!",
          text: "Player status and account type have been updated successfully.",
          heightAuto: false,
        });
        window.location.reload();
      } catch (error) {
        console.error("Error changing status:", error);
        await Swal.fire({
          title: "Error",
          text: "Failed to update player status. Please try again.",
          icon: "error",
          heightAuto: false,
        });
      }
    },
    async handleBannedPlayer(user_id) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "This action will update chat restrictions for this player.",
        icon: "warning",
        heightAuto: false,
        showCancelButton: true,
        confirmButtonText: "Yes, proceed!",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) return;

      try {
        const note = this.blockReason === "Other" ? this.customNote : "";
        const reason = this.blockReason;
        const block_minutes = Number(this.blockTime) || 0;

        if (reason === "Other" && note === "") {
          await Swal.fire({
            title: "Missing reason",
            text: "Please specify a reason before proceeding.",
            icon: "warning",
            heightAuto: false,
          });
          return;
        }

        await postFetch(`/messagebanned/${user_id}`, {
          is_banned_chat: !this.playerInfo.is_banned_chat,
          reason,
          user_id,
          note,
          is_active: true,
          block_minutes,
        });

        await Swal.fire({
          title: "Success!",
          text: "Chat restriction status updated successfully.",
          heightAuto: false,
        });

        this.blockReason = "Spam";
        this.customNote = "";
        this.blockTime = 0;

        window.location.reload();
      } catch (error) {
        console.error("Error updating player ban:", error);
        await Swal.fire({
          title: "Something went wrong",
          text: "Failed to update ban status. Please try again later.",
          icon: "error",
          heightAuto: false,
        });
      }
    },
  },
};
</script>
